/**
 * Схема `countries.v1.json` (zod) и выведенные из неё типы.
 *
 * Каждая точка ряда — кортеж [год, значение] или [год, значение, пометка].
 * Пометка опускается у фактов (компактная форма: ~97 % точек в файле —
 * факты, строка 'fact' в каждой из них давала +1 МБ к размеру); оценки
 * всегда несут явный флаг 'estimate'. Читать флаг следует через `pointFlag`.
 * Значения обязаны быть конечными числами, чтобы NaN/Infinity никогда не
 * попали в движок.
 */
import { z } from 'zod';
import { INDICATORS, type Indicator } from './indicators';

export type Flag = 'fact' | 'estimate';
export type Point = [year: number, value: number] | [year: number, value: number, flag: Flag];

/** Пометка точки: без третьего элемента — факт. */
export function pointFlag(p: Readonly<Point>): Flag {
  return p[2] === 'estimate' ? 'estimate' : 'fact';
}
export type WhoRegion = 'AFR' | 'AMR' | 'SEAR' | 'EUR' | 'EMR' | 'WPR';

export interface CountryRecord {
  iso3: string;
  iso2: string;
  nameRu: string;
  whoRegion: WhoRegion;
  series: Record<Indicator, Point[]>;
}

export interface Dataset {
  version: number;
  builtAt: string;
  countries: CountryRecord[];
}

export const WHO_REGIONS = ['AFR', 'AMR', 'SEAR', 'EUR', 'EMR', 'WPR'] as const satisfies readonly WhoRegion[];

export const FlagSchema: z.ZodType<Flag> = z.enum(['fact', 'estimate']);

const YearSchema = z.number().int().min(1900).max(2200);
const ValueSchema = z.number().finite();

export const PointSchema: z.ZodType<Point> = z.union([
  z.tuple([YearSchema, ValueSchema]),
  z.tuple([YearSchema, ValueSchema, FlagSchema]),
]);

export const WhoRegionSchema: z.ZodType<WhoRegion> = z.enum(WHO_REGIONS);

const seriesShape: Record<Indicator, z.ZodType<Point[]>> = Object.fromEntries(
  INDICATORS.map((ind) => [ind, z.array(PointSchema)]),
) as unknown as Record<Indicator, z.ZodType<Point[]>>;

export const SeriesSchema: z.ZodType<Record<Indicator, Point[]>> = z.object(seriesShape);

export const CountryRecordSchema: z.ZodType<CountryRecord> = z.object({
  iso3: z.string().regex(/^[A-Z]{3}$/),
  iso2: z.string().regex(/^[A-Z]{2}$/),
  nameRu: z.string().min(1),
  whoRegion: WhoRegionSchema,
  series: SeriesSchema,
});

export const DatasetSchema: z.ZodType<Dataset> = z.object({
  version: z.number().int().positive(),
  builtAt: z.string().datetime(),
  countries: z.array(CountryRecordSchema),
});
